import { Divider } from '@ds';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { Form } from '@ui';
import { toast } from 'react-toastify';
import { fromZonedTime } from 'date-fns-tz';
import { OCSTimeFields } from '../components/OCSTimeFields';
import { CaseFacility, OffOCSPayload, OrganType, Randomization } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { retrieveUnifiedError } from '@/api/utils';
import { useOffOcs } from '@/queries/case';
import { OCSForm } from '../types';
import { getOcsDefaultValues, offOcsSchema } from './config';
import { isRandomizationScs } from '@/utils/helpers';

type OffOCSFormProps = {
  caseId: Uuid;
  ocsStartTimestamp?: string;
  txCenter?: CaseFacility;
  organType: OrganType;
  randomization?: Randomization;
  onOpenChange: () => void;
};

export const OffOCSRegularForm = ({
  caseId,
  ocsStartTimestamp,
  txCenter,
  organType,
  randomization,
  onOpenChange,
}: OffOCSFormProps) => {
  const { mutateAsync } = useOffOcs();

  const handleSubmit = async (values: OCSForm) => {
    const { date, time, timeZone } = values;
    const timestamp = fromZonedTime(new Date(`${date} ${time}`), timeZone).toISOString();
    let payload: OffOCSPayload = { timestamp, timeZone };
    if (organType === OrganType.Lungs) {
      payload = {
        perfusionTerminationDataItem: {
          doubleLungFlush: { flushTime: { timestamp, timeZone } },
        },
      };
    }
    try {
      await mutateAsync({
        caseId,
        data: payload,
      });
      onOpenChange();
      toast.success('Status successfully updated');
    } catch (error) {
      const { message } = retrieveUnifiedError(error);
      toast.error(message);
    }
  };

  const isScs = isRandomizationScs(randomization);

  return (
    <Form
      defaultValues={getOcsDefaultValues(txCenter, ocsStartTimestamp)}
      schema={offOcsSchema}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-6">
        <OCSTimeFields
          heading={isScs ? 'Confirmed SCS Handoff Time' : 'Confirmed organ off OCS Time'}
          subHeading={isScs ? '' : 'Flush Time'}
          dateFieldName="date"
          timeFieldName="time"
          timezoneFieldName="timeZone"
          ocsStartTime={ocsStartTimestamp}
          compactView
          showPerfusionTime={!isScs}
          footerText={isScs ? '' : 'At time of reading'}
        />
        <Divider />
        <div className="flex justify-end gap-2">
          <FormActions
            actions={{
              size: 'md',
              discard: {
                label: 'Discard',
                handler: onOpenChange,
              },
              submit: {
                label: 'Update status',
              },
            }}
          />
        </div>
      </div>
    </Form>
  );
};
